import React, { useState, useEffect, useContext, useRef } from 'react';
import AppContext from '../../contexts/AppContext';
import { User, MapPin, Calendar, MessageCircle, Trophy, X, Send, Users, Image, Lock, UserX, Trash2 } from 'lucide-react';
import * as firestoreService from '../../services/firestoreService';
import { uploadMessageMedia } from '../../services/storageService';
import SecurityUtils from '../../utils/security';
import { validateVideoDuration, fixVideoOrientation } from '../../utils/media';
import { formatTime12hr, formatEventDate } from '../../utils/formatting';
import Avatar from '../../components/common/Avatar';

const EventDetailView = ({ eventId }) => {
  const { events, setEvents, users, currentUser, setView, messages, setMessages, showAlert, showConfirm } = useContext(AppContext);
  const [activeTab, setActiveTab] = useState('chat');
  const [newMessage, setNewMessage] = useState('');
  const [evtMediaFile, setEvtMediaFile] = useState(null);
  const [evtMediaPreview, setEvtMediaPreview] = useState(null);
  const [evtMediaType, setEvtMediaType] = useState(null);
  const [evtSendingMedia, setEvtSendingMedia] = useState(false);
  const [evtViewMedia, setEvtViewMedia] = useState(null);
  const [fetchedEvent, setFetchedEvent] = useState(null);
  const evtMediaInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const evtMessagesContainerRef = useRef(null);

  const localEvent = events.find(e => e.id === eventId);
  const event = localEvent || fetchedEvent;

  useEffect(() => {
    if (!localEvent) {
      firestoreService.getEvent(eventId).then(e => {
        if (e) setFetchedEvent(e);
      }).catch(() => {});
    }
  }, [eventId, localEvent]);

  const isAttendee = event?.attendees?.includes(currentUser.id);
  const isOwner = event?.userId === currentUser.id;
  const organizer = users.find(u => u.id === event?.userId);
  const attendeeUsers = (event?.attendees || []).map(id => users.find(u => u.id === id)).filter(Boolean);

  // Real-time messages listener for event chat
  useEffect(() => {
    if (!event?.groupChatId || !isAttendee) return;
    const unsub = firestoreService.onMessagesChange(event.groupChatId, (convMsgs) => {
      setMessages(prev => {
        const otherMsgs = prev.filter(m => m.conversationId !== event.groupChatId);
        return [...otherMsgs, ...convMsgs];
      });
      firestoreService.markConversationAsRead(event.groupChatId, currentUser.id).catch(() => {});
    });
    firestoreService.markConversationAsRead(event.groupChatId, currentUser.id).catch(() => {});
    return () => unsub();
  }, [event?.groupChatId, isAttendee, currentUser.id, setMessages]);

  const eventMessages = messages.filter(m => m.conversationId === event?.groupChatId) || [];

  // Scroll to bottom when entering chat tab or when messages change
  useEffect(() => {
    if (activeTab === 'chat' && eventMessages.length > 0) {
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'auto' }), 100);
    }
  }, [activeTab, eventMessages.length]);

  const handleEvtMediaSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const isVideo = file.type.startsWith('video/');
    const validation = SecurityUtils.validateFile(file, { maxSize: isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024 });
    if (!validation.valid) { showAlert(`File validation failed: ${validation.error}`, 'Error'); return; }
    if (validation.type !== 'image' && validation.type !== 'video') { showAlert('Please select an image or video file.', 'Error'); return; }
    if (isVideo) {
      const validDuration = await validateVideoDuration(file, 15);
      if (!validDuration) { showAlert('Video must be 15 seconds or shorter.', 'Error'); return; }
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setEvtMediaFile(file);
      setEvtMediaPreview(reader.result);
      setEvtMediaType(isVideo ? 'video' : 'image');
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const clearEvtMedia = () => {
    setEvtMediaFile(null);
    setEvtMediaPreview(null);
    setEvtMediaType(null);
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() && !evtMediaFile) return;
    if (!event?.groupChatId) return;
    const sanitizedMessage = newMessage.trim() ? SecurityUtils.sanitizeText(newMessage) : '';
    setNewMessage('');
    const currentMediaFile = evtMediaFile;
    const currentMediaType = evtMediaType;
    clearEvtMedia();
    try {
      setEvtSendingMedia(!!currentMediaFile);
      let mediaUrl = null;
      if (currentMediaFile) {
        const fileToUpload = currentMediaType === 'video' ? await fixVideoOrientation(currentMediaFile) : currentMediaFile;
        const result = await uploadMessageMedia(fileToUpload, currentUser.id, event.groupChatId);
        mediaUrl = result.url;
      }
      await firestoreService.sendMessage({
        conversationId: event.groupChatId,
        senderId: currentUser.id,
        content: sanitizedMessage,
        ...(mediaUrl ? { mediaUrl, mediaType: currentMediaType } : {}),
      });
    } catch (err) {
      console.error('Error sending message:', err);
    } finally {
      setEvtSendingMedia(false);
    }
  };

  if (!event) {
    return (
      <div className="max-w-2xl mx-auto p-4 text-center">
        <p className="text-gray-400">Loading event...</p>
      </div>
    );
  }

  // Access control for private events
  const isInvitedToEvent = event.invitedUsers?.some(inv =>
    (typeof inv === 'string' ? inv : inv.userId) === currentUser.id && (typeof inv === 'string' || inv.status !== 'declined')
  );
  if (event.visibility === 'private' && !isOwner && !isAttendee && !isInvitedToEvent) {
    return (
      <div className="max-w-2xl mx-auto p-4 text-center">
        <button onClick={() => setView('events')} className="text-[#16a34a] hover:underline mb-4 inline-block">&larr; Back to Events</button>
        <div className="bg-black rounded-lg shadow border border-gray-700 p-12">
          <Lock className="w-12 h-12 mx-auto mb-3 text-gray-500" />
          <h3 className="text-lg font-bold text-white mb-2">Private Event</h3>
          <p className="text-gray-400">This event is private. You need an invitation to access it.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-4">
      <button onClick={() => setView('events')} className="text-[#16a34a] hover:underline mb-4 inline-block">&larr; Back to Events</button>

      {/* Event Info Card */}
      <div className="bg-black rounded-lg shadow border border-gray-700 p-6 mb-4">
        <h2 className="text-xl font-bold text-white mb-2">
          {event.title}
          {event.visibility === 'private' && (
            <span className="ml-2 inline-flex items-center space-x-1 px-2 py-0.5 bg-gray-700 text-gray-300 text-xs rounded-full font-medium align-middle">
              <Lock className="w-3 h-3" />
              <span>Private</span>
            </span>
          )}
        </h2>
        {event.description && <p className="text-gray-300 text-sm mb-3">{event.description}</p>}
        <div className="space-y-1 text-sm text-gray-400">
          <p className="flex items-center space-x-2"><Calendar className="w-4 h-4" /><span>{formatEventDate(event.date)}{event.time ? ` at ${formatTime12hr(event.time)}` : ''}</span></p>
          {event.courseName && <p className="flex items-center space-x-2"><Trophy className="w-4 h-4" /><span>{event.courseName}</span></p>}
          {event.address && <p className="flex items-center space-x-2"><MapPin className="w-4 h-4" /><span>{typeof event.address === 'object' ? event.address.address : event.address}</span></p>}
          <p className="flex items-center space-x-2"><Users className="w-4 h-4" /><span>{attendeeUsers.length} player{attendeeUsers.length !== 1 ? 's' : ''}</span></p>
          {organizer && <p className="flex items-center space-x-2"><User className="w-4 h-4" /><span>Organized by {organizer.name}</span></p>}
        </div>
        {isOwner && (
          <button
            onClick={async () => {
              if (!await showConfirm('Are you sure you want to delete this event? This cannot be undone.', 'Delete Event', 'Delete', true)) return;
              try {
                if (event.groupChatId) {
                  await firestoreService.sendMessage({
                    conversationId: event.groupChatId,
                    senderId: 'system',
                    content: 'Event was deleted by the owner.',
                  });
                  await firestoreService.updateConversation(event.groupChatId, { disabled: true });
                }
                await firestoreService.deleteEvent(event.id);
                setEvents(prev => prev.filter(e => e.id !== event.id));
                setView('events');
              } catch (err) {
                console.error('Error deleting event:', err);
                showAlert('Failed to delete event. Please try again.', 'Error');
              }
            }}
            className="mt-4 w-full px-3 py-2 bg-red-600 text-pure-white rounded-lg hover:bg-red-700 flex items-center justify-center space-x-2 text-sm font-medium"
          >
            <Trash2 className="w-4 h-4" />
            <span>Delete Event</span>
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-black rounded-lg shadow border border-gray-700 p-4">
        <div className="flex space-x-1 mb-4 bg-gray-900 rounded-lg p-1">
          <button onClick={() => setActiveTab('chat')} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${activeTab === 'chat' ? 'bg-[#16a34a] text-pure-white' : 'text-gray-400 hover:text-white'}`}>
            <span className="flex items-center justify-center space-x-1"><MessageCircle className="w-4 h-4" /><span>Chat</span></span>
          </button>
          <button onClick={() => setActiveTab('players')} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${activeTab === 'players' ? 'bg-[#16a34a] text-pure-white' : 'text-gray-400 hover:text-white'}`}>
            <span className="flex items-center justify-center space-x-1"><Users className="w-4 h-4" /><span>Players ({attendeeUsers.length})</span></span>
          </button>
        </div>

        {/* Chat Tab */}
        {activeTab === 'chat' && (
          <div>
            {!isAttendee ? (
              <div className="text-center py-12">
                <MessageCircle className="w-12 h-12 mx-auto mb-3 text-gray-500" />
                <p className="text-gray-400">Join the event to access the chat</p>
              </div>
            ) : (
              <div className="flex flex-col">
                <div ref={evtMessagesContainerRef} className="h-[400px] overflow-y-auto p-3 space-y-3 bg-[rgb(var(--color-bg-primary))] rounded-lg">
                  {eventMessages.length === 0 && (
                    <div className="text-center text-gray-500 py-8">
                      <MessageCircle className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No messages yet. Start the conversation!</p>
                    </div>
                  )}
                  {eventMessages.map(msg => {
                    if (msg.senderId === 'system') {
                      return (
                        <div key={msg.id} className="flex justify-center my-2">
                          <p className="text-xs text-gray-400 bg-gray-800/50 px-3 py-1 rounded-full italic">{msg.content}</p>
                        </div>
                      );
                    }
                    const isMine = msg.senderId === currentUser.id;
                    const sender = users.find(u => u.id === msg.senderId);
                    return (
                      <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                        {!isMine && <div className="flex-shrink-0 self-start mt-1 mr-1"><Avatar user={sender} size="xs" /></div>}
                        <div className={`max-w-[75%] ${isMine ? 'bg-[#16a34a] text-pure-white' : 'bg-[rgb(var(--color-bg-tertiary))] text-[rgb(var(--color-text-primary))]'} rounded-2xl px-3 py-2`}>
                          {!isMine && sender && <p className="text-xs font-bold mb-1 opacity-80">{sender.name}</p>}
                          {msg.mediaUrl && msg.mediaType === 'image' && (
                            <img src={msg.mediaUrl} alt="" className="rounded-lg max-w-full max-h-60 mb-1 cursor-pointer" onClick={() => setEvtViewMedia({ url: msg.mediaUrl, type: 'image' })} />
                          )}
                          {msg.mediaUrl && msg.mediaType === 'video' && (
                            <video src={msg.mediaUrl} controls playsInline className="rounded-lg max-w-full max-h-60 mb-1 cursor-pointer" onClick={(e) => { e.preventDefault(); setEvtViewMedia({ url: msg.mediaUrl, type: 'video' }); }} />
                          )}
                          {msg.content && <p className="text-sm break-words">{msg.content}</p>}
                          <p className="text-xs mt-1 opacity-70">{new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                        </div>
                        {isMine && <div className="flex-shrink-0 self-start mt-1 ml-1"><Avatar user={currentUser} size="xs" /></div>}
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
                <div className="p-3 border-t border-gray-700 bg-black overflow-hidden">
                  {evtMediaPreview && (
                    <div className="mb-2 relative inline-block">
                      {evtMediaType === 'image' ? (
                        <img src={evtMediaPreview} alt="Preview" className="max-h-32 rounded-lg" />
                      ) : (
                        <video src={evtMediaPreview} className="max-h-32 rounded-lg" controls playsInline />
                      )}
                      <button onClick={clearEvtMedia} className="absolute -top-2 -right-2 bg-red-600 text-pure-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-700">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                  {evtSendingMedia && (
                    <p className="text-xs text-gray-400 mb-1">Uploading media...</p>
                  )}
                  <div className="flex space-x-2">
                    <button onClick={() => evtMediaInputRef.current?.click()} className="p-3 text-gray-400 hover:text-white flex-shrink-0" title="Attach photo or video">
                      <Image className="w-5 h-5" />
                    </button>
                    <input ref={evtMediaInputRef} type="file" accept="image/*,video/*" onChange={handleEvtMediaSelect} className="hidden" />
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && !evtSendingMedia && handleSendMessage()}
                      placeholder="Type a message..."
                      className="flex-1 min-w-0 px-4 py-3 border border-gray-700 rounded-full focus:ring-2 focus:ring-[#22c55e] text-white bg-gray-900 text-sm"
                    />
                    <button onClick={handleSendMessage} disabled={(!newMessage.trim() && !evtMediaFile) || evtSendingMedia} className="p-3 bg-[#16a34a] text-pure-white rounded-full hover:bg-[#15803d] disabled:opacity-50 flex-shrink-0">
                      <Send className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Players Tab */}
        {activeTab === 'players' && (
          <div className="space-y-3">
            {attendeeUsers.map(user => {
              const userIsOwner = user.id === event.userId;
              return (
                <div key={user.id} className="flex items-center justify-between p-2 hover:bg-gray-900 rounded-lg transition-colors">
                  <button onClick={() => setView('user-profile-' + user.id)} className="flex items-center space-x-3 flex-1 text-left">
                    <Avatar user={user} size="md" />
                    <div>
                      <div className="flex items-center space-x-2">
                        <p className="text-white font-medium">{user.name}</p>
                        {userIsOwner && (
                          <span className="px-2 py-0.5 bg-yellow-600 text-pure-white text-xs rounded-full font-bold">Organizer</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-400">@{user.handle}</p>
                    </div>
                  </button>
                  {isOwner && !userIsOwner && (
                    <button
                      onClick={async () => {
                        if (!await showConfirm(`Remove ${user.name} from this event?`, 'Remove Player', 'Remove', true)) return;
                        try {
                          await firestoreService.leaveEvent(event.id, user.id);
                          if (event.groupChatId) {
                            await firestoreService.removeParticipant(event.groupChatId, user.id);
                            await firestoreService.sendMessage({
                              conversationId: event.groupChatId,
                              senderId: 'system',
                              content: `${user.name} was removed from the event.`,
                            });
                          }
                          setEvents(prev => prev.map(e => e.id === event.id ? { ...e, attendees: e.attendees.filter(id => id !== user.id) } : e));
                        } catch (err) {
                          console.error('Error removing user:', err);
                          showAlert('Failed to remove user. Please try again.', 'Error');
                        }
                      }}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-gray-800 rounded-lg transition-colors"
                      title={`Remove ${user.name}`}
                    >
                      <UserX className="w-5 h-5" />
                    </button>
                  )}
                </div>
              );
            })}
            {attendeeUsers.length === 0 && (
              <div className="text-center py-8">
                <Users className="w-10 h-10 mx-auto mb-2 text-gray-500 opacity-50" />
                <p className="text-gray-400 text-sm">No players have joined yet</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Media Modal */}
      {evtViewMedia && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setEvtViewMedia(null)}>
          <button onClick={() => setEvtViewMedia(null)} className="absolute top-4 right-4 text-white hover:text-gray-300 z-50">
            <X className="w-8 h-8" />
          </button>
          <div onClick={(e) => e.stopPropagation()} className="max-w-full max-h-full">
            {evtViewMedia.type === 'image' ? (
              <img src={evtViewMedia.url} alt="" className="max-w-full max-h-[90vh] object-contain rounded-lg" />
            ) : (
              <video src={evtViewMedia.url} controls autoPlay playsInline className="max-w-full max-h-[90vh] rounded-lg" />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDetailView;
